import React, { useEffect, useMemo, useState } from "react";
import { SaveBtn, TopHeading, Wrapper } from "../../../common/index.style";
import { CustomContainer } from "./index.style";
import {
  Col,
  Form,
  ColorPicker,
  Input,
  InputNumber,
  Checkbox,
  Radio,
  Row,
  Select,
  Table,
  Button,
  Card,
  message,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addpublisher } from "../../../redux/publisherSlice";
import {
  convertColorObjectToHex,
  copyToClipboard,
  hsvToHexWithAlpha,
  textSizeTags,
} from "../../../utils/helper";
import { AiOutlinePercentage } from "react-icons/ai";
import { TbCurrencyDollar } from "react-icons/tb";
import { FiCopy } from "react-icons/fi";
import lookup from "country-code-lookup";
import { getSupportedCurrencies } from "../../../redux/currencySlice";
import { TEXTS } from "../../../utils/text";

const CMS = {
  Beehiiv: 'Beehiiv',
  Ghost: 'Ghost',
  WordPress: 'WordPress',
  Direct: 'Direct'
}

// Map onboarding platform values to CMS constants
const platformToCMS = {
  Ghost: CMS.Ghost,
  beehiiv: CMS.Beehiiv,
  Beehiiv: CMS.Beehiiv,
  WordPress: CMS.WordPress,
  Direct: CMS.Direct,
};

// Stripe minimum charge amounts per currency
const stripeMinimums = {
  usd: 0.50, aed: 2.00, ars: 0.50, aud: 0.50, brl: 0.50,
  cad: 0.50, chf: 0.50, cop: 0.50, czk: 15.00, dkk: 2.50,
  eur: 0.50, gbp: 0.30, hkd: 4.00, huf: 175.00, idr: 0.50,
  ils: 0.50, inr: 0.50, jpy: 50, krw: 50, mxn: 10,
  myr: 2.00, nok: 3.00, nzd: 0.50, php: 0.50, pln: 2.00,
  ron: 2.00, rub: 0.50, sek: 3.00, sgd: 0.50, thb: 10,
  zar: 0.50, bgn: 0.50,
};

// Reverse lookup: find iso2 by country name
const findCountryIso2 = (countryName) => {
  if (!countryName) return null;
  try {
    const result = lookup.byCountry(countryName);
    return result?.iso2 || null;
  } catch {
    // Try case-insensitive search
    const found = lookup.countries.find(
      (c) => c.country.toLowerCase() === countryName.toLowerCase()
    );
    return found?.iso2 || null;
  }
};

const AddPublishers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { loading: isPublisherLoading } = useSelector(
    (store) => store.publisher
  );

  const onboardingRequest = location.state?.onboardingRequest || null;

  const [activeCMS, setActiveCMS] = useState(); // Default to Beehive
  const handleCmsChange = (e) => {
    if (e.target?.value ) {
      setActiveCMS(e.target?.value);
      // Ghost defaults to the alternative button position (next to Subscribe)
      form.setFieldsValue({ isButtonNext: e.target.value === CMS.Ghost });
    }
  };

  const { loading: isSupportedCurrenciesLoading, supportedCurrencies } =
    useSelector((store) => store.currency);

  useEffect(() => {
    dispatch(getSupportedCurrencies());
  }, [dispatch]);

  // Prefill form from onboarding request
  useEffect(() => {
    if (onboardingRequest) {
      const cms = platformToCMS[onboardingRequest.platform] || null;
      const countryIso2 = findCountryIso2(onboardingRequest.country);
      const currency = onboardingRequest.currency
        ? onboardingRequest.currency.toLowerCase()
        : null;

      const prefillValues = {
        website: onboardingRequest.website || undefined,
        email: onboardingRequest.email || undefined,
        articlePrice: onboardingRequest.pricePerArticle || undefined,
        active: "Yes",
      };

      // Parse name into firstName / lastName
      if (onboardingRequest.name) {
        const nameParts = onboardingRequest.name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          prefillValues.firstName = nameParts[0];
          prefillValues.lastName = nameParts.slice(1).join(' ');
        } else {
          prefillValues.firstName = nameParts[0];
        }
      }

      if (countryIso2) {
        prefillValues.country = countryIso2;
      }
      if (currency) {
        prefillValues.currency = currency;
        prefillValues.miniPrice = stripeMinimums[currency] ?? 0.50;
      }
      if (cms) {
        prefillValues.activeCMS = cms;
        setActiveCMS(cms);
        // Ghost defaults to the alternative button position (next to Subscribe)
        prefillValues.isButtonNext = cms === CMS.Ghost;

        if (cms === CMS.Ghost) {
          if (onboardingRequest.ghostAdminApiKey) {
            prefillValues.apiKey = onboardingRequest.ghostAdminApiKey;
          }
          if (onboardingRequest.ghostApiUrl) {
            prefillValues.apiURL = onboardingRequest.ghostApiUrl;
          }
        }
        if (cms === CMS.Beehiiv) {
          if (onboardingRequest.beehiivApiKey) {
            prefillValues.beehiivApiKey = onboardingRequest.beehiivApiKey;
          }
          if (onboardingRequest.beehiivPublicationId) {
            prefillValues.beehiivPublicationId =
              onboardingRequest.beehiivPublicationId;
          }
        }
      }

      form.setFieldsValue(prefillValues);
    }
  }, [onboardingRequest, form]);

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const onFinish = (values) => {
    const paymentButtonColor =
      values?.paymentButtonColor?.metaColor?.originalInput;
    const paymentTextColor = values?.textColor?.metaColor?.originalInput;
    const actaPromptText =
      values?.actaPromptText === undefined ||
        values?.actaPromptText === TEXTS.EMPTY_TEXT
        ? TEXTS.DEFAULT_ACTA_PROMPT_TEXT
        : values?.actaPromptText;
    const actaButtonText =
      values?.actaButtonText === undefined ||
        values?.actaButtonText === TEXTS.EMPTY_TEXT
        ? TEXTS.DEFAULT_ACTA_BUTTON_TEXT
        : values?.actaButtonText;

    const publisherData = {
      name: values?.publisherName,
      email: values?.email,
      website: values?.website,
      userFirstName: values?.firstName,
      userLastName: values?.lastName,
      country: values?.country,
      articlePrice: values?.articlePrice,
      revShare1: {
        percentage: values?.revShare,
        minPrice: values?.miniPrice,
      },
      revShare2: {
        percentage: values?.revShare2 ?? null,
        minPrice: values?.lowPrice2 ?? null,
        maxPrice: values?.highPrice2 ?? null,
      },
      revShare3: {
        percentage: values?.revShare3 ?? null,
        minPrice: values?.lowPrice3 ?? null,
        maxPrice: values?.highPrice3 ?? null,
      },
      productRevShare1: {
        percentage: values?.productRevShare ?? null,
      },
      productRevShare2: {
        percentage: values?.productRevShare2 ?? null,
        minPrice: values?.productLowPrice2 ?? null,
        maxPrice: values?.productHighPrice2 ?? null,
      },
      productRevShare3: {
        percentage: values?.productRevShare3 ?? null,
        minPrice: values?.productLowPrice3 ?? null,
        maxPrice: values?.productHighPrice3 ?? null,
      },
      stripe: {
        currency: values?.currency,
      },
      active: values?.active === "Yes" ? true : false,
      viewEventActive: values?.viewEvent === "Yes" ? true : false,
      checkoutemail: values?.checkoutemail === "Yes" ? true : false,
      subscribeOverride: !!values?.subscribeOverride,
      activeCMS: values.activeCMS,
      beehiivAPI: {
        apiKey: values.beehiivApiKey,
        publicationId: values.beehiivPublicationId,
      },
      ghostAPI: {
        apiKey: values?.apiKey,
        apiURL: values?.apiURL,
      },
      ghostMemberOptIn: !!values?.ghostMemberOptIn,
      ghostEmailPrefill: !!values?.ghostEmailPrefill,
      wordpressAPI: {
        siteUrl: values?.website || null,
        username: null,
        applicationPassword: null,
        // pluginEndpoint and pluginSecretKey are set by the WordPress plugin auto-connection,
        // not by the admin form.
      },
      style: {
        paywallButtonClass: values?.paywallButtonClass,
        paywallPadding: values?.paywallPadding,
        paywallButtonColor: convertColorObjectToHex(values?.paywallButtonColor),
        paywallButtonTextColor: convertColorObjectToHex(
          values?.paywallButtonTextColor
        ),
        paywallButtonTextSize: values?.paywallButtonTextSize ?? null,
        paywallContainerClass: values?.paywallContainerClass,
        paywallPromptTextClass: values?.paywallPromptTextClass,
        paywallPromptTextColor: convertColorObjectToHex(
          values?.paywallPromptTextColor
        ),
        paywallPromptTextSize: values?.paywallPromptTextSize ?? null,
        paywallButtonTag: values?.paywallButtonTag ?? null,
        paywallButtonWrapperClass: values?.paywallButtonWrapperClass ?? null,
        ghContentClass: values?.ghContentClass ?? null,
        isButtonNext:
          values?.isButtonNext ??
          (!values?.activeCMS || values?.activeCMS === CMS.Ghost),
        paywallButtonStyles: values.paywallButtonStyles ?? null,
        paywallPromptTextStyles: values.paywallPromptTextStyles ?? null,
      },
      primaryColor: paymentButtonColor
        ? typeof paymentButtonColor === "object" &&
          Object.keys(paymentButtonColor)?.length > 0
          ? hsvToHexWithAlpha(
            paymentButtonColor?.h,
            paymentButtonColor?.s,
            paymentButtonColor?.v,
            paymentButtonColor?.a
          )
          : "#" + paymentButtonColor
        : "#4209EE",
      secondaryColor: paymentTextColor
        ? typeof paymentTextColor === "object" &&
          Object.keys(paymentTextColor)?.length > 0
          ? hsvToHexWithAlpha(
            paymentTextColor?.h,
            paymentTextColor?.s,
            paymentTextColor?.v,
            paymentTextColor?.a
          )
          : "#" + paymentTextColor
        : "#FFFFFF",
      actaPromptText: actaPromptText,
      actaButtonText: actaButtonText,
    };
    dispatch(addpublisher({ publisherData, navigate }));
  };

  const countries = useMemo(() => {
    const countries = lookup.countries;
    const countryOptions = [];
    countries.forEach((country) => {
      countryOptions.push({
        label: country.country,
        value: country.iso2,
      });
    });
    return countryOptions;
  }, []);

  const currencies = useMemo(() => {
    const displayNames = new Intl.DisplayNames(["en"], { type: "currency" });
    const getName = (code) => {
      try { return displayNames.of(code.toUpperCase()) || code.toUpperCase(); }
      catch { return code.toUpperCase(); }
    };
    const pinnedCodes = ["usd", "eur", "gbp"];
    const pinnedSet = new Set(pinnedCodes);
    const pinned = pinnedCodes.map((code) => ({
      label: `${code.toUpperCase()} — ${getName(code)}`,
      value: code.toLowerCase(),
    }));
    const rest = supportedCurrencies
      .filter((c) => !pinnedSet.has(c.toLowerCase()))
      .map((code) => ({
        label: `${code.toUpperCase()} — ${getName(code)}`,
        value: code.toLowerCase(),
      }));
    return [
      { label: "Popular", options: pinned },
      { label: "All currencies", options: rest },
    ];
  }, [supportedCurrencies]);

  const paywallButtonTags = [
    { label: "a", value: "a" },
    { label: "button", value: "button" },
    { label: "div", value: "div" },
  ];

  // Build onboarding reference table data
  const onboardingTableData = useMemo(() => {
    if (!onboardingRequest) return [];
    const rows = [
      { key: "name", field: "Name", value: onboardingRequest.name },
      { key: "email", field: "Email", value: onboardingRequest.email },
      { key: "website", field: "Website", value: onboardingRequest.website },
      { key: "country", field: "Country", value: onboardingRequest.country },
      { key: "currency", field: "Currency", value: onboardingRequest.currency },
      {
        key: "pricePerArticle",
        field: "Article Price",
        value: onboardingRequest.pricePerArticle,
      },
      {
        key: "platform",
        field: "CMS Platform",
        value: onboardingRequest.platform,
      },
      {
        key: "referredBy",
        field: "Referred By",
        value: onboardingRequest.referredBy,
      },
    ];

    if (
      onboardingRequest.platform === "Ghost" ||
      onboardingRequest.platform === "ghost"
    ) {
      rows.push(
        {
          key: "ghostAdminApiKey",
          field: "Ghost Admin API Key",
          value: onboardingRequest.ghostAdminApiKey,
        },
        {
          key: "ghostApiUrl",
          field: "Ghost API URL",
          value: onboardingRequest.ghostApiUrl,
        }
      );
    }

    if (
      onboardingRequest.platform === "beehiiv" ||
      onboardingRequest.platform === "Beehiiv"
    ) {
      rows.push(
        {
          key: "beehiivApiKey",
          field: "beehiiv API Key",
          value: onboardingRequest.beehiivApiKey,
        },
        {
          key: "beehiivPublicationId",
          field: "beehiiv Publication ID",
          value: onboardingRequest.beehiivPublicationId,
        }
      );
    }

    return rows.filter((r) => r.value != null && r.value !== "");
  }, [onboardingRequest]);

  const onboardingTableColumns = [
    {
      title: "Field",
      dataIndex: "field",
      key: "field",
      width: 200,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (text) => (
        <span style={{ wordBreak: "break-all" }}>{String(text)}</span>
      ),
    },
    {
      title: "",
      key: "copy",
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<FiCopy />}
          onClick={() =>
            copyToClipboard(String(record.value ?? ""), messageApi)
          }
          size="small"
        />
      ),
    },
  ];

  const renderStyleConfig = () => {
    if (activeCMS === CMS.Beehiiv) {
      return renderBeehiivStyleConfig()
    }
    if (activeCMS === CMS.WordPress) {
      return renderWordPressStyleConfig()
    }

    return renderGhostStyleConfig()
  }

  const renderGhostStyleConfig = () => {
    return (
      <>
        <Row gutter={[16, 0]}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallContainerClass"
              label="Paywall container class"
              rules={[{ required: true, message: "required" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonClass"
              label="Paywall button class"
              rules={[{ required: true, message: "required" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextClass"
              label="Paywall prompt text class"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextSize"
              label="Paywall prompt text size"
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={textSizeTags}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonWrapperClass"
              label="Paywall button wrapper class"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTag"
              label="Paywall button tag"
              rules={[{ required: true, message: "required" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={paywallButtonTags}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTextSize"
              label="Paywall button text size"
            >
              <InputNumber min={1} suffix={<p>px</p>} />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="paywallPadding" label="Paywall Padding">
              <InputNumber min={1} suffix={<p>px</p>} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col lg={{ span: 6 }}>
            <Form.Item name="ghContentClass" label="Gh content class">
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaPromptText" label="Acta prompt text">
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaButtonText" label="Acta button text">
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="isButtonNext"
              valuePropName="checked"
              label="Button alternative position"
            >
              <Checkbox />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 0]}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTextColor"
              label="Paywall button text color"
            >
              <ColorPicker allowClear showText defaultValue={null} />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="paywallButtonColor" label="Paywall button color">
              <ColorPicker allowClear showText defaultValue={null} />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextColor"
              label="Paywall prompt text color"
            >
              <ColorPicker allowClear defaultValue={null} showText />
            </Form.Item>
          </Col>
          {/* Payment iframe button colors — commented out to use defaults; re-enable if custom colors needed
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paymentButtonColor"
              initialValue="#4209EE"
              label="Payment button color"
            >
              <ColorPicker defaultValue="#4209EE" showText />
            </Form.Item>
          </Col>
          */}
        </Row>
        {/* Payment iframe button colors — commented out to use defaults; re-enable if custom colors needed
        <Row gutter={[16, 0]}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="textColor"
              initialValue="#FFFFFF"
              label="Outline / text color"
            >
              <ColorPicker showText />
            </Form.Item>
          </Col>
        </Row>
        */}
      </>
    )
  }

  const renderBeehiivStyleConfig = () => {
    return (
      <Row gutter={[16, 0]}>
        <Col lg={{ span: 12 }}>
          <Form.Item
            name="paywallContainerClass"
            label="Paywall container class"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col lg={{ span: 12 }}>
          <Form.Item
            name="paywallButtonStyles"
            label="Paywall button class"
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    )
  }

  const renderWordPressStyleConfig = () => {
    return (
      <>
        <Row gutter={28}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallContainerClass"
              label="Paywall container class"
              rules={[{ required: true, message: "required" }]}
            >
              <Input placeholder="e.g. .memberpress-paywall" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextClass"
              label="Paywall prompt text class"
            >
              <Input placeholder="e.g. wp-block-heading" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextStyles"
              label="Prompt style"
            >
              <Input placeholder="e.g. font-size:1.2rem;font-weight:600" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaPromptText" label="Prompt text">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonClass"
              label="Paywall button class"
            >
              <Input placeholder="e.g. wp-block-button__link" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonStyles"
              label="Button style"
            >
              <Input placeholder="e.g. background:#000;border-radius:4px" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaButtonText" label="Button text">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          {/* Payment iframe button colors — commented out to use defaults; re-enable if custom colors needed
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paymentButtonColor"
              initialValue="#4209EE"
              label="Payment button color"
            >
              <ColorPicker defaultValue="#4209EE" showText />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="textColor"
              initialValue="#FFFFFF"
              label="Outline / text color"
            >
              <ColorPicker showText />
            </Form.Item>
          </Col>
          */}
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonColor"
              label="Button color (override)"
              initialValue={null}
            >
              <ColorPicker allowClear showText />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTextColor"
              label="Button text color (override)"
              initialValue={null}
            >
              <ColorPicker allowClear showText />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="isButtonNext" valuePropName="checked" label="Button placed next to subscribe">
              <Checkbox />
            </Form.Item>
          </Col>
          <Col lg={{ span: 8 }}>
            <Form.Item
              name="subscribeOverride"
              valuePropName="checked"
              label="Pay-per-article only (replace subscribe gate)"
            >
              <Checkbox />
            </Form.Item>
          </Col>
        </Row>
      </>
    )
  }

  const renderCmsConfig = () => {
    return (
      <>
        <Form.Item
          name="activeCMS" label="CMS Type"
          rules={[{ required: true, message: "required" }]}
        >
          <Radio.Group onChange={handleCmsChange} value={activeCMS}>
            <Radio value={CMS.Ghost}>{CMS.Ghost}</Radio>
            <Radio value={CMS.Beehiiv}>{CMS.Beehiiv}</Radio>
            <Radio value={CMS.WordPress}>{CMS.WordPress}</Radio>
            <Radio value={CMS.Direct}>{CMS.Direct}</Radio>
          </Radio.Group>
        </Form.Item>
        {activeCMS === CMS.Beehiiv && (
          <Row gutter={[16, 0]}>
            <Col lg={{ span: 12 }}>
              <Form.Item
                name="beehiivApiKey"
                label="API Key"
                rules={[{ required: true, message: "required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col lg={{ span: 12 }}>
              <Form.Item
                name="beehiivPublicationId"
                label="Publication ID"
                rules={[
                  { required: true, message: "required" },
                  { type: "string" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        )}
        {activeCMS === CMS.Ghost && (
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Row gutter={[16, 0]}>
                <Col lg={{ span: 10 }}>
                  <Form.Item
                    name="ghostMemberOptIn"
                    valuePropName="checked"
                    label={'Show "Join as a free member" checkbox'}
                    tooltip="Adds the buyer to this publisher's Ghost members and subscribes them to the newsletter."
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
                <Col lg={{ span: 10 }}>
                  <Form.Item
                    name="ghostEmailPrefill"
                    valuePropName="checked"
                    label="Prefill email for logged-in Ghost members"
                    tooltip="Looks up the logged-in Ghost member on the publisher's page so the paywall can skip the email step."
                  >
                    <Checkbox />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col lg={{ span: 12 }}>
              <Form.Item
                name="apiKey"
                label="API Key"
                rules={[{ required: true, message: "required" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col lg={{ span: 12 }}>
              <Form.Item
                name="apiURL"
                label="API URL"
                rules={[
                  { required: true, message: "required" },
                  { type: "url", message: "Please enter a valid URL" },
                  { type: "string" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        )}
        {activeCMS === CMS.WordPress && (
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <div style={{
                padding: "12px 16px",
                background: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: 6,
              }}>
                <p style={{ margin: 0, fontSize: 13 }}>
                  <strong>Plugin connection is automatic.</strong> After creating the publisher, share
                  their Publisher ID. They'll enter it in WordPress → Settings → Acta → "Connect to Acta".
                </p>
              </div>
            </Col>
          </Row>
        )}
      </>
    )
  }

  return (
    <Wrapper>
      {contextHolder}
      <CustomContainer>
        <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ actaButtonText: TEXTS.DEFAULT_ACTA_BUTTON_TEXT, actaPromptText: TEXTS.DEFAULT_ACTA_PROMPT_TEXT, isButtonNext: true, ghostMemberOptIn: true, ghostEmailPrefill: true }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <TopHeading style={{ margin: 0 }}>Add Publisher</TopHeading>
            <SaveBtn htmlType="button">
              {isPublisherLoading ? "loading..." : "Save"}
            </SaveBtn>
          </div>

          {onboardingRequest && (
            <Card
              title="Onboarding Request Reference"
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Table
                dataSource={onboardingTableData}
                columns={onboardingTableColumns}
                pagination={false}
                size="small"
                bordered
              />
            </Card>
          )}

          {/* Publisher Details */}
          <Card title="Publisher Details" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="publisherName"
                  label="Publisher Name"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  name="website"
                  label="Website"
                  rules={[
                    { required: true, message: "required" },
                    { type: "url", message: "Please enter a valid URL" },
                    { type: "string" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { type: "email", message: "email is not valid" },
                    { required: true, message: "required" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Location & Status (Country/Currency are Stripe Connect dependent — fields unchanged) */}
          <Card title="Location & Status" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={14} lg={8}>
                <Form.Item
                  name="country"
                  label="Country"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={countries}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10} lg={6}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={isSupportedCurrenciesLoading}
                    options={currencies}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item
                  name="active"
                  label="Active?"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Radio.Group>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} sm={10} lg={6}>
                <Form.Item
                  name="viewEvent"
                  label="Page View Event Active?"
                  initialValue="Yes"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Radio.Group>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item
                  name="checkoutemail"
                  label="Checkout Email?"
                  initialValue="Yes"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Radio.Group>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Article Pricing & Revenue Share */}
          <Card title="Article Pricing & Revenue Share" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} lg={6}>
                <Form.Item
                  name="articlePrice"
                  label="Default Article Price"
                  rules={[{ required: true, message: "required" }]}
                >
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item
                  name="revShare"
                  label="Rev Share"
                  extra="Defaults to 15% if left blank"
                >
                  <InputNumber prefix={<AiOutlinePercentage />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={6}>
                <Form.Item
                  name="miniPrice"
                  label="Minimum Price"
                  rules={[{ required: true, message: "required" }]}
                >
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="revShare2" label="Rev Share 2">
                  <InputNumber prefix={<AiOutlinePercentage />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="lowPrice2" label="Low Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="highPrice2" label="High Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="revShare3" label="Rev Share 3">
                  <InputNumber prefix={<AiOutlinePercentage />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="lowPrice3" label="Low Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="highPrice3" label="High Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Product Pricing & Revenue Share */}
          <Card title="Product Pricing & Revenue Share" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item
                  name="productRevShare"
                  label="Rev Share"
                  extra="Defaults to 4% if left blank"
                >
                  <InputNumber prefix={<AiOutlinePercentage />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="productRevShare2" label="Rev Share 2">
                  <InputNumber prefix={<AiOutlinePercentage />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="productLowPrice2" label="Low Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="productHighPrice2" label="High Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="productRevShare3" label="Rev Share 3">
                  <InputNumber prefix={<AiOutlinePercentage />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="productLowPrice3" label="Low Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8} lg={4}>
                <Form.Item name="productHighPrice3" label="High Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* CMS Configuration */}
          <Card title="CMS Configuration" style={{ marginBottom: 24 }}>
            {renderCmsConfig()}
          </Card>

          {/* Styles */}
          <Card title="Styles">
            {renderStyleConfig()}
          </Card>
        </Form>
      </CustomContainer>
    </Wrapper>
  );
};

export default AddPublishers;
